/**
 * Image crop utilities for use with react-easy-crop (or any crop UI that provides pixel crop area).
 * Used by ImageCropModal to produce a cropped image Blob from an image source and crop rect.
 */

/**
 * Load an image from a URL (object URL or data URL) and return an Image element.
 * @param {string} url - Object URL (e.g. from URL.createObjectURL(file)) or data URL
 * @returns {Promise<HTMLImageElement>}
 */
export function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => reject(new Error('Failed to load image')));
    image.src = url;
  });
}

/**
 * Produce a cropped image Blob from the source image and crop area (in original image pixels).
 * @param {string} imageSrc - Object URL or data URL of the image
 * @param {Object} pixelCrop - Crop area in original image coordinates: { x, y, width, height }
 * @param {Object} [options] - Output options
 * @param {string} [options.format='image/jpeg'] - MIME type for output
 * @param {number} [options.quality=0.9] - Compression quality (0–1) for JPEG/WebP
 * @param {number} [options.maxWidth] - Optional max width (maintains aspect ratio)
 * @param {number} [options.maxHeight] - Optional max height (maintains aspect ratio)
 * @returns {Promise<Blob>} - Cropped image blob
 */
export async function getCroppedImg(imageSrc, pixelCrop, options = {}) {
  const { format = 'image/jpeg', quality = 0.9, maxWidth, maxHeight } = options;

  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');

  let { width, height } = pixelCrop;
  let drawWidth = width;
  let drawHeight = height;
  let sx = pixelCrop.x;
  let sy = pixelCrop.y;
  let sw = width;
  let sh = height;

  // Optional resize to max dimensions (e.g. for smaller file size)
  if (maxWidth || maxHeight) {
    const scale = Math.min(
      maxWidth ? maxWidth / width : 1,
      maxHeight ? maxHeight / height : 1
    );
    if (scale < 1) {
      drawWidth = Math.round(width * scale);
      drawHeight = Math.round(height * scale);
    }
  }

  canvas.width = drawWidth;
  canvas.height = drawHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(
    image,
    sx, sy, sw, sh,  // source rect (original image crop)
    0, 0, drawWidth, drawHeight  // destination rect (canvas)
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create image blob'));
      },
      format,
      quality
    );
  });
}
